import React, { useMemo, forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { MqDesktop, Desktop } from 'streamr-ui/css/consts'
import Picture from './Picture'
import Bio from './Bio'
import IndexLabel from './IndexLabel'
import ArrowCursor from '~shared/ArrowCursor'

const Inner = styled.div`
    position: relative;
`

const Root = styled.div`
    @media (max-width: ${Desktop - 1}px) {
        transform: none !important;
    }

    ${IndexLabel} {
        position: absolute;
    }

    ${Bio},
    ${Picture} {
        box-sizing: border-box;
        padding: 0 1rem;
    }

    ${Picture} {
        opacity: 0.2;
        transition: 0.2s ease-in-out opacity;
    }

    ${Picture} ${IndexLabel} {
        bottom: 1em;
        right: 1em;
    }

    ${Bio} {
        opacity: 0;
        transition: 0s ease-in-out;
        transition-delay: 0s, 0.2s;
        transition-property: opacity, visibility;
        visibility: hidden;
    }

    ${Bio} ${IndexLabel} {
        background-color: transparent;
        border-radius: 0;
        bottom: 0;
        color: #323232;
        display: none;
        line-height: 1;
        padding: 0;
    }

    @media ${MqDesktop} {
        ${Picture} ${IndexLabel} {
            display: none;
        }

        ${Bio} {
            height: 100%;
            position: absolute;
            right: -100%;
            top: 0;
            width: 100%;
            z-index: 0;
        }

        ${Bio} ${IndexLabel} {
            display: block;
        }
    }

    ${({ current }) =>
        !!current &&
        css`
            ${Picture} {
                opacity: 1;
            }
        `}

    ${({ current, idle }) =>
        !!current &&
        !!idle &&
        css`
            ${Bio} {
                opacity: 1;
                transition-duration: 0.2s;
                transition-delay: 0.3s;
                visibility: visible;
            }
        `}

    ${({ idle }) =>
        !!idle &&
        css`
            @media ${MqDesktop} {
                transition: 0.2s linear transform;
            }
        `}
`

const WrappedPicture = forwardRef(function WrappedPicture({ className, style, ...props }, ref) {
    return (
        <div className={className} style={style} ref={ref}>
            <Picture {...props} />
        </div>
    )
})

const UnstyledMember = ({
    style,
    active,
    data,
    idle,
    memberNumber,
    onPictureClick,
    totalMembers,
    arrow,
    className,
}) => {
    const indexLabel = useMemo(
        () => <IndexLabel current={memberNumber} total={totalMembers} active={!!active} />,
        [memberNumber, totalMembers, active],
    )

    return (
        <Root className={className} current={active} idle={idle} style={style}>
            <Inner data-swipeable>
                <ArrowCursor
                    $appearDirection="none"
                    as={WrappedPicture}
                    direction={arrow}
                    alt={data.name}
                    memberId={data.id}
                    onClick={onPictureClick}
                >
                    {indexLabel}
                </ArrowCursor>
                <Bio member={data}>{indexLabel}</Bio>
            </Inner>
        </Root>
    )
}

const Member = styled(UnstyledMember)``

export default Member
